export const FUZZY_APPLIED_MAX_WORD_RATIO = 0.1
export const FUZZY_APPLIED_MAX_WORDS_CHANGED = 8

const CACHE_MAX_ENTRIES = 2000
const CACHE_KEY_SEPARATOR = "\u0000"
const cache = new Map()

/**
 * Determines whether a normalized plain-text value is close enough to a target
 * to be considered the same content with minor edits. The edit budget is a
 * dual bound: 10% of the longer word count, capped at 8 words, so short fields
 * stay near-exact and long content cannot absorb large rewrites.
 *
 * When `alternative` is provided (the recommendation's source text), the value
 * must also be strictly closer to the target than to the alternative —
 * otherwise a small recommendation whose old and new text sit within the edit
 * budget of each other would classify untouched content as applied.
 *
 * Inputs must already be normalized (HTML stripped, whitespace collapsed) by
 * the caller.
 *
 * @param {string} value
 * @param {string} target
 * @param {string} [alternative]
 * @returns {boolean}
 */
export function isWithinFuzzyWordDistance(value, target, alternative = null) {
    const valueText = `${value ?? ""}`
    const targetText = `${target ?? ""}`
    const alternativeText = alternative == null ? null : `${alternative}`

    if (valueText === targetText) return true
    if (!valueText || !targetText) return false

    const key = [valueText, targetText, alternativeText ?? ""].join(CACHE_KEY_SEPARATOR)
    const cached = cache.get(key)
    if (cached !== undefined) return cached

    const result = computeIsWithinFuzzyWordDistance(valueText, targetText, alternativeText)
    if (cache.size >= CACHE_MAX_ENTRIES) {
        cache.delete(cache.keys().next().value)
    }
    cache.set(key, result)
    return result
}

export function __resetFuzzyCacheForTests() {
    cache.clear()
}

function computeIsWithinFuzzyWordDistance(valueText, targetText, alternativeText) {
    const valueWords = splitWords(valueText)
    const targetWords = splitWords(targetText)
    const maxWordCount = Math.max(valueWords.length, targetWords.length)
    if (maxWordCount === 0) return true

    const budget = Math.min(Math.ceil(FUZZY_APPLIED_MAX_WORD_RATIO * maxWordCount), FUZZY_APPLIED_MAX_WORDS_CHANGED)

    const targetDistance = wordEditDistanceWithin(valueWords, targetWords, budget)
    if (targetDistance === null) return false
    if (alternativeText === null) return true

    // The value must be strictly closer to the target than to the alternative; an
    // equidistant value is ambiguous and must not be treated as a match.
    const alternativeDistance = wordEditDistanceWithin(valueWords, splitWords(alternativeText), targetDistance)
    return alternativeDistance === null
}

function splitWords(text) {
    return text.split(/\s+/).filter(Boolean)
}

// Banded Levenshtein over word tokens, returning the distance when it is within
// `budget` and null otherwise. Only cells within `budget` of the diagonal can
// hold a distance <= budget, so cost is O(maxWords * budget).
function wordEditDistanceWithin(leftWords, rightWords, budget) {
    const leftLength = leftWords.length
    const rightLength = rightWords.length
    if (Math.abs(leftLength - rightLength) > budget) return null

    const overBudget = budget + 1
    let previousRow = new Array(rightLength + 1).fill(overBudget)
    for (let j = 0; j <= Math.min(rightLength, budget); j++) previousRow[j] = j

    for (let i = 1; i <= leftLength; i++) {
        const currentRow = new Array(rightLength + 1).fill(overBudget)
        let rowMinimum = overBudget

        if (i <= budget) {
            currentRow[0] = i
            rowMinimum = i
        }

        const bandStart = Math.max(1, i - budget)
        const bandEnd = Math.min(rightLength, i + budget)

        for (let j = bandStart; j <= bandEnd; j++) {
            const substitutionCost = leftWords[i - 1] === rightWords[j - 1] ? 0 : 1
            const candidate = Math.min(previousRow[j] + 1, currentRow[j - 1] + 1, previousRow[j - 1] + substitutionCost)
            currentRow[j] = Math.min(candidate, overBudget)
            if (currentRow[j] < rowMinimum) rowMinimum = currentRow[j]
        }

        if (rowMinimum > budget) return null
        previousRow = currentRow
    }

    return previousRow[rightLength] <= budget ? previousRow[rightLength] : null
}
